import debounce from 'lodash/debounce'
import {
	consolidateTypewriterParagraphIntoSingleLine,
	ensureWhitespaceBetweenAdjacentWordElements,
	getLineElementsForWrap,
	getTextAnimationBlockElements,
	normalizeWhitespaceOnlyTextNodes,
} from '../split_line_wrap'
import { ensureLayPretextTextAnimationLoaded, someVisibleTargetNeedsPretextLazyModule } from '../pretext_lazy'
import { applyLineMaskPaddingBottomStyle } from '../line_mask_padding_bottom'
import {
	applyPreRevealVisualStateToTarget,
	taResizeTrace,
	taResizeTraceTarget,
	taSplitLog,
	taSplitLogTarget,
} from '../text_animation_resplit_recovery'
import {
	waitForTextAnimationFonts,
	whenTextAnimationFontsAreReady,
} from '../text_animation_font_readiness'
import { getCurrentTargetsForPageOverlays } from './targets'
let selector = '.lay-text-animation'
import { initObserver } from './textanimations_pageoverlays'

const PENDING_RESPLIT_ATTR = 'data-lay-ta-pending-resplit'
const PRETEXT_WIDTH_INSET_ATTR = 'data-lay-pretext-width-inset'
let lateFontResplitScheduled = false

const NO_LINE_MASK_ANIMATION_TYPES = new Set([
	'fade_characters',
	'typewriter_lines',
	'typewriter_words',
	'typewriter',
	'typewriter_fade',
	'fade_and_slide_lines',
	'fade_and_slide_words',
	'fade_and_slide_characters',
	'fade_and_slide_whole_text',
])

function shouldUseLineMaskForAnimationType(animationType) {
	return !NO_LINE_MASK_ANIMATION_TYPES.has(animationType)
}

function waitForTextAnimationFontsIfNeeded(targets) {
	if (!someVisibleTargetNeedsPretextLazyModule(targets)) {
		return Promise.resolve(true)
	}
	return waitForTextAnimationFonts()
}

function scheduleResplitAfterLateFonts() {
	if (lateFontResplitScheduled) {
		return
	}
	lateFontResplitScheduled = true
	whenTextAnimationFontsAreReady()
		.then(() => {
			lateFontResplitScheduled = false
			markDirtyTextAnimationsPendingResplitOverlay({
				reason: 'fonts_ready_after_timeout',
				skipIfWidthUnchanged: false,
			})
			return prepareTexts()
		})
		.catch(() => {
			lateFontResplitScheduled = false
		})
}

function markDirtyTextAnimationsPendingResplitOverlay(options = {}) {
	const { reason = 'unknown', skipIfWidthUnchanged = true } = options
	const w = window.innerWidth
	if (skipIfWidthUnchanged && w === oldWindowWidth) {
		if (reason === 'window_width_changed') {
			taResizeTrace('[overlay] markPendingResplit skipped (innerWidth unchanged)', { innerWidth: w, reason })
		}
		return
	}
	const targets = getCurrentTargetsForPageOverlays()
	let marked = 0
	let skipNotVisible = 0
	let skipNotDirty = 0
	let skipNoOriginal = 0
	targets.forEach((target) => {
		if (!jQuery(target).is(':visible')) {
			skipNotVisible++
			return
		}
		if (jQuery(target).data('text-state') !== 'dirty') {
			skipNotDirty++
			return
		}
		if (!jQuery(target).data('original-html')) {
			skipNoOriginal++
			return
		}
		target.setAttribute(PENDING_RESPLIT_ATTR, '1')
		marked++
	})
	taResizeTrace('[overlay] markPendingResplit', {
		reason,
		fromWidth: oldWindowWidth,
		toWidth: w,
		targetCount: targets.length,
		marked,
		skipNotVisible,
		skipNotDirty,
		skipNoOriginal,
	})
	oldWindowWidth = w
}

const markTextAnimationsPendingResplit = () => {
	markDirtyTextAnimationsPendingResplitOverlay({ reason: 'window_width_changed', skipIfWidthUnchanged: true })
}

const onLayTextAnimSizeChangedOverlay = (newSize) => {
	taResizeTrace('[overlay] sizechanged → mark pending resplit', { newSize })
	markDirtyTextAnimationsPendingResplitOverlay({ reason: 'sizechanged', skipIfWidthUnchanged: false })
	debouncedResizeEnd()
}

function copyLayTextAnimationAttrsToGhost(target, ghost) {
	ghost.className = target.className
	const names = [
		'data-animation-type',
		'data-animation-easing',
		'data-stagger-easing',
		'data-top',
		'data-duration',
		'data-stagger',
		'data-delay',
		'data-line-mask-padding-bottom',
		PRETEXT_WIDTH_INSET_ATTR,
		'lang',
	]
	names.forEach((name) => {
		const v = target.getAttribute(name)
		if (v != null && v !== '') {
			ghost.setAttribute(name, v)
		}
	})
}

function copyTextMeasurementStylesFromTarget(target, ghost) {
	const cs = window.getComputedStyle(target)
	ghost.style.boxSizing = cs.boxSizing || 'border-box'
	ghost.style.width = `${Math.max(1, target.clientWidth)}px`
	ghost.style.padding = cs.padding
	ghost.style.font = cs.font
	ghost.style.letterSpacing = cs.letterSpacing
	ghost.style.wordSpacing = cs.wordSpacing
	ghost.style.textTransform = cs.textTransform
	ghost.style.lineHeight = cs.lineHeight
	ghost.style.textAlign = cs.textAlign
}

function atomicResplitLayTextAnimation(target) {
	const originalHTML = jQuery(target).data('original-html')
	if (!originalHTML) {
		return
	}

	const ghost = document.createElement('div')
	copyLayTextAnimationAttrsToGhost(target, ghost)
	copyTextMeasurementStylesFromTarget(target, ghost)
	ghost.style.position = 'fixed'
	ghost.style.left = '-99999px'
	ghost.style.top = '0'
	ghost.style.visibility = 'hidden'
	ghost.style.pointerEvents = 'none'

	ghost.innerHTML = originalHTML
	document.body.appendChild(ghost)

	splitText(ghost)
	wrapLines(ghost)

	target.innerHTML = ''
	target['🍌'] = null
	while (ghost.firstChild) {
		target.appendChild(ghost.firstChild)
	}
	ghost.remove()
	taSplitLogTarget(target, '[overlay] atomicResplitLayTextAnimation: swapped DOM → preReveal')
	applyPreRevealVisualStateToTarget(target)
}

function getPretextLayoutOverflowPx(target) {
	const paragraphs = getTextAnimationBlockElements(target)
	let overflowPx = 0
	for (let i = 0; i < paragraphs.length; i++) {
		const paragraph = paragraphs[i]
		if (paragraph.dataset.pretextSplit !== 'true') {
			continue
		}
		const maxWidth = Math.max(paragraph.clientWidth, Math.round(paragraph.getBoundingClientRect().width))
		if (!maxWidth) {
			continue
		}
		const lines = paragraph.querySelectorAll('.lay-text-line')
		for (let j = 0; j < lines.length; j++) {
			const line = lines[j]
			overflowPx = Math.max(overflowPx, line.scrollWidth - maxWidth)
		}
	}
	return Math.max(0, overflowPx)
}

function waitForNextFrame() {
	return new Promise((resolve) => {
		window.requestAnimationFrame(() => resolve())
	})
}

async function stabilizePostLayoutIfNeeded(target) {
	if (!target || !jQuery(target).data('original-html')) {
		return
	}
	for (let attempts = 0; attempts < 2; attempts++) {
		await waitForNextFrame()
		await waitForNextFrame()
		const overflowPx = getPretextLayoutOverflowPx(target)
		taSplitLogTarget(target, '[overlay] stabilizePostLayoutIfNeeded', { attempt: attempts + 1, overflowPx })
		if (overflowPx <= 1) {
			return
		}
		const previousInset = parseFloat(target.getAttribute(PRETEXT_WIDTH_INSET_ATTR)) || 0
		target.setAttribute(PRETEXT_WIDTH_INSET_ATTR, String(Math.ceil(previousInset + overflowPx + 1)))
		atomicResplitLayTextAnimation(target)
	}
}

/**
 * Replaces a `.word` span's text content with `.char` spans (one per
 * character). Used after `Splitting({ by: 'lines' })` for `masked_characters`
 * when Pretext bails — splitting words manually keeps the surrounding DOM
 * (e.g. the parent `<a class="laybutton">`) untouched, so the link survives.
 */
const splitWordIntoCharSpans = (word) => {
	const text = word.textContent || ''
	if (!text) return
	word.innerHTML = ''
	let charIndex = 0
	for (const ch of text) {
		const isWhitespace = /\s/.test(ch)
		const span = document.createElement('span')
		span.className = isWhitespace ? 'whitespace' : 'char'
		if (!isWhitespace) span.setAttribute('data-char', ch)
		span.style.setProperty('--char-index', charIndex)
		span.textContent = ch
		word.appendChild(span)
		charIndex++
	}
	word.style.setProperty('--char-total', charIndex)
}

// Function to split text into lines or characters based on animation type
const splitText = (target) => {
	const pt = window.layPretextTextAnimation
	const paragraphs = getTextAnimationBlockElements(target)

	// Check animation type to determine splitting method
	const animationType = target.getAttribute('data-animation-type')
	const splitBy = animationType === 'typewriter' || animationType === 'typewriter_fade' ? 'chars' : 'lines'
	const useLineMaskForType = shouldUseLineMaskForAnimationType(animationType)

	// Split each paragraph individually
	paragraphs.forEach((paragraph) => {
		if (pt && pt.shouldUsePretextForAnimationType(animationType) && pt.splitParagraphWithPretext(paragraph, useLineMaskForType)) {
			return
		}
		if (pt && pt.shouldUsePretextWordLevelAnimation(animationType) && pt.splitParagraphWithPretextThenWords(paragraph, useLineMaskForType)) {
			return
		}
		if (pt && pt.shouldUsePretextCharLevelAnimation(animationType)) {
			const useMaskForChars = animationType === 'masked_characters'
			if (pt.splitParagraphWithPretextThenChars(paragraph, useMaskForChars)) {
				return
			}
		}
		const splittingOptions = { target: paragraph, by: splitBy }
		if (splitBy === 'chars') {
			splittingOptions.whitespace = true
		}
		const results = Splitting(splittingOptions)
		// Character-level animations need `.char`
		// spans inside each `.word`. The Pretext path already produces them;
		// when Pretext bails, we land here with `splitBy='lines'`, which gives
		// us words + line indices but no chars. Add chars per-word manually
		// while preserving inline wrappers.
		if ((animationType === 'masked_characters' || animationType === 'fade_characters' || animationType === 'fade_and_slide_characters') && splitBy === 'lines') {
			paragraph.querySelectorAll('.word').forEach((word) => {
				splitWordIntoCharSpans(word)
			})
		}
		return results
	})
}
let useLineMask = true

// Function to wrap lines in divs
const wrapLines = (target) => {
	const paragraphs = getTextAnimationBlockElements(target)

	// Check animation type to determine wrapping strategy
	const animationType = target.getAttribute('data-animation-type')
	const useLineMaskForType = useLineMask && shouldUseLineMaskForAnimationType(animationType)
	
	if (animationType === 'typewriter' || animationType === 'typewriter_fade') {
		paragraphs.forEach((paragraph) => {
			consolidateTypewriterParagraphIntoSingleLine(paragraph)
		})
		return
	}

	// Original line wrapping logic for other animation types
	paragraphs.forEach((paragraph) => {
		if (paragraph.dataset.pretextSplit === 'true') {
			return
		}

		normalizeWhitespaceOnlyTextNodes(paragraph)
		const words = paragraph.querySelectorAll('.word')

		const lines = []
		words.forEach((word) => {
			const lineIndex = word.style.getPropertyValue('--line-index')
			if (!lines[lineIndex]) lines[lineIndex] = []
			lines[lineIndex].push(word)
		})

		// Wrap each line in a div with class 'lay-text-line'
		lines.forEach((lineWords, i) => {
			const wrapper = document.createElement('div')
			wrapper.className = 'lay-text-line'

			const lineElements = getLineElementsForWrap(paragraph, i)

			// Insert wrapper before first element of the line
			if (lineElements.length > 0) {
				lineElements[0].parentNode.insertBefore(wrapper, lineElements[0])
				// Move all elements into the wrapper
				lineElements.forEach((el) => wrapper.appendChild(el))
			}
		})

		// Remove br elements after wrapping is complete
		const brs = paragraph.querySelectorAll('br')
		brs.forEach((br) => br.remove())

		// Remove any whitespace spans that are between line divs
		const whitespaces = paragraph.querySelectorAll('.whitespace')
		whitespaces.forEach((whitespace) => {
			const prevElement = whitespace.previousElementSibling
			const nextElement = whitespace.nextElementSibling
			if (prevElement && nextElement && prevElement.classList.contains('lay-text-line') && nextElement.classList.contains('lay-text-line')) {
				whitespace.remove()
			}
		})

		// Wrap each line div in a line-mask div if useLineMask is true
		if (useLineMaskForType) {
			const lineDivs = paragraph.querySelectorAll('.lay-text-line')
			lineDivs.forEach((lineDiv) => {
				const maskWrapper = document.createElement('div')
				maskWrapper.className = 'lay-text-line-mask'
				applyLineMaskPaddingBottomStyle(maskWrapper, target)
				lineDiv.parentNode.insertBefore(maskWrapper, lineDiv)
				maskWrapper.appendChild(lineDiv)
			})
		}

		ensureWhitespaceBetweenAdjacentWordElements(paragraph)
	})
}

/**
 * Page-overlay equivalent of split.js `stripSplitMarkupIfPresent`. Same
 * reasoning: the AJAX-cached overlay HTML can contain previous split
 * markup; Splitting library throws when re-splitting it.
 */
const stripSplitMarkupIfPresent = (target) => {
	if (!target) return false
	if (
		!target.querySelector('.lay-text-line') &&
		!target.querySelector('.word') &&
		!target.querySelector('.char') &&
		!target.querySelector('.whitespace') &&
		!target.querySelector('.lay-text-line-mask')
	) {
		return false
	}
	const selectorsInOrder = ['.char', '.word', '.whitespace', '.lay-text-line-mask', '.lay-text-line']
	selectorsInOrder.forEach((sel) => {
		Array.from(target.querySelectorAll(sel)).forEach((node) => {
			const parent = node.parentNode
			if (!parent) return
			while (node.firstChild) {
				parent.insertBefore(node.firstChild, node)
			}
			parent.removeChild(node)
		})
	})
	target.querySelectorAll('p.words, h1.words, h2.words, h3.words, h4.words, h5.words, h6.words').forEach((p) => {
		p.classList.remove('words')
		if (p.style && p.style.removeProperty) p.style.removeProperty('--word-total')
		if (p.getAttribute('style') === '') p.removeAttribute('style')
		if (p.getAttribute('class') === '') p.removeAttribute('class')
	})
	target.normalize()
	return true
}

const storeOriginalTexts = () => {
	const targets = getCurrentTargetsForPageOverlays()

	targets.forEach((target) => {
		// Store original HTML if not already stored
		if (!jQuery(target).data('original-html')) {
			// Same reasoning as split.js: AJAX-restored overlay HTML may
			// already contain split markup. Strip first so prepareTexts
			// has a clean slate (Splitting library throws on re-splits).
			stripSplitMarkupIfPresent(target)
			const originalHTML = target.innerHTML

			jQuery(target).data('original-html', originalHTML)
			jQuery(target).data('text-state', 'original')
		}
	})
}

// Function to prepare texts
const prepareTexts = async (triggerEvent = true) => {
	const targets = getCurrentTargetsForPageOverlays()
	let didMutateTargets = false
	let anyResizeMutation = false
	let anyFirstSplitMutation = false
	const resizeMutationTargets = new Set()
	const stabilizationTasks = []

	taSplitLog('[overlay] prepareTexts start', {
		triggerEvent,
		targetCount: targets.length,
	})
	taResizeTrace('[overlay] prepareTexts start', {
		triggerEvent,
		targetCount: targets.length,
		innerWidth: typeof window !== 'undefined' ? window.innerWidth : null,
	})

	const fontsReadyBeforeSplit = await waitForTextAnimationFontsIfNeeded(targets)

	if (someVisibleTargetNeedsPretextLazyModule(targets)) {
		try {
			await ensureLayPretextTextAnimationLoaded(targets)
		} catch (e) {
			console.warn('[Lay] Pretext text animation chunk failed to load; using Splitting fallback.', e)
		}
	}

	targets.forEach((target) => {
		if (!jQuery(target).is(':visible')) {
			target.removeAttribute(PENDING_RESPLIT_ATTR)
			taResizeTraceTarget(target, '[overlay] prepareTexts branch', {
				branch: 'skip_not_visible',
				hadPending: target.hasAttribute(PENDING_RESPLIT_ATTR),
			})
			taSplitLogTarget(target, '[overlay] prepareTexts: skip (not :visible)')
			return
		}

		const pendingResizeResplit = target.hasAttribute(PENDING_RESPLIT_ATTR)

		if (pendingResizeResplit) {
			target.removeAttribute(PENDING_RESPLIT_ATTR)
			target.removeAttribute(PRETEXT_WIDTH_INSET_ATTR)
			const anim = target.getAttribute('data-animation-type')
			taResizeTraceTarget(target, '[overlay] prepareTexts branch', {
				branch: 'pending_resize_resplit',
				anim,
				textState: jQuery(target).data('text-state'),
			})
			taSplitLogTarget(target, '[overlay] prepareTexts branch: pendingResizeResplit', { anim })
			if (anim === 'fade_whole_text' || anim === 'fade_and_slide_whole_text') {
				const originalHTML = jQuery(target).data('original-html')
				if (originalHTML) {
					target.innerHTML = originalHTML
					target['🍌'] = null
				}
				jQuery(target).data('text-state', 'dirty')
				didMutateTargets = true
				anyResizeMutation = true
				resizeMutationTargets.add(target)
				applyPreRevealVisualStateToTarget(target)
			} else {
				atomicResplitLayTextAnimation(target)
				jQuery(target).data('text-state', 'dirty')
				didMutateTargets = true
				anyResizeMutation = true
				resizeMutationTargets.add(target)
				stabilizationTasks.push(stabilizePostLayoutIfNeeded(target))
			}
			return
		}

		if (jQuery(target).data('text-state') === 'original') {
			const anim = target.getAttribute('data-animation-type')
			taResizeTraceTarget(target, '[overlay] prepareTexts branch', {
				branch: 'first_split_original',
				anim,
			})
			taSplitLogTarget(target, '[overlay] prepareTexts branch: first split (text-state original)', { anim })
			if (anim === 'fade_whole_text' || anim === 'fade_and_slide_whole_text') {
				jQuery(target).data('text-state', 'dirty')
			} else {
				anyFirstSplitMutation = true
				splitText(target)
				wrapLines(target)
				jQuery(target).data('text-state', 'dirty')
				didMutateTargets = true
				taSplitLogTarget(target, '[overlay] prepareTexts: after wrapLines → preReveal')
				applyPreRevealVisualStateToTarget(target)
				stabilizationTasks.push(stabilizePostLayoutIfNeeded(target))
			}
		} else {
			taResizeTraceTarget(target, '[overlay] prepareTexts branch', {
				branch: 'skip_dirty_no_pending',
				textState: jQuery(target).data('text-state'),
			})
			taSplitLogTarget(target, '[overlay] prepareTexts: skip (already dirty / no pending)', {
				textState: jQuery(target).data('text-state'),
			})
		}
	})
	if (stabilizationTasks.length > 0) {
		taSplitLog('[overlay] prepareTexts: await stabilization', { taskCount: stabilizationTasks.length })
		await Promise.all(stabilizationTasks)
	}
	const willEmit = !!(triggerEvent && didMutateTargets)
	const fromResizeOnly = !!(didMutateTargets && anyResizeMutation && !anyFirstSplitMutation)
	taSplitLog('[overlay] prepareTexts end', {
		didMutateTargets,
		emitTextsAreSplit: willEmit,
		stabilizationRan: stabilizationTasks.length > 0,
		fromResizeOnly,
		resizeMutationTargetCount: resizeMutationTargets.size,
	})
	if (triggerEvent && didMutateTargets) {
		taSplitLog('[overlay] prepareTexts: emit texts_are_split_for_page_overlays', {
			fromResize: fromResizeOnly,
			resizeMutationTargetCount: resizeMutationTargets.size,
		})
		taResizeTrace('[overlay] prepareTexts emit texts_are_split_for_page_overlays', {
			fromResize: fromResizeOnly,
			anyFirstSplitMutation,
			anyResizeMutation,
			resizeMutationTargetCount: resizeMutationTargets.size,
		})
		window.laytheme.emit('texts_are_split_for_page_overlays', {
			fromResize: fromResizeOnly,
			resizeMutationTargets,
		})
	}
	if (!fontsReadyBeforeSplit) {
		scheduleResplitAfterLateFonts()
	}
}

window.laytheme.on('page_overlay_display_block', function ($target) {
	prepareTexts().catch(() => {})
})
window.laytheme.on('overlayshown_animation_ended', function ($target) {
	// Initialize observer for all elements
	if (window.currentPoTaObservers) {
		window.currentPoTaObservers()
	}
	window.currentPoTaObservers = initObserver()
})

const debouncedResizeEnd = debounce(() => {
	taResizeTrace('[overlay] debouncedResizeEnd fired → prepareTexts', {
		innerWidth: typeof window !== 'undefined' ? window.innerWidth : null,
	})
	prepareTexts().catch(() => {})
}, 200)

let oldWindowWidth = window.innerWidth

// Initialize resize handling
const initResizeHandling = () => {
	jQuery(window).off('window_width_changed', markTextAnimationsPendingResplit)
	jQuery(window).off('window_width_changed', debouncedResizeEnd)

	jQuery(window).on('window_width_changed', markTextAnimationsPendingResplit)
	jQuery(window).on('window_width_changed', debouncedResizeEnd)

	if (window.laytheme && typeof window.laytheme.off === 'function' && typeof window.laytheme.on === 'function') {
		window.laytheme.off('sizechanged', onLayTextAnimSizeChangedOverlay)
		window.laytheme.on('sizechanged', onLayTextAnimSizeChangedOverlay)
	}
}
initResizeHandling()

// Initialize text splitting
var setup = function () {
	// Clean up any existing observers
	if (window.currentPoTaObservers) {
		window.currentPoTaObservers()
	}
	// page overlays :D
	column_selectors = ['.lay-overlay ' + selector]

	// Initialize text splitting for all matching elements
	const targets = Array.from(document.querySelectorAll(column_selectors))
	if (targets.length > 0) {
		storeOriginalTexts()
		prepareTexts().catch(() => {})
	}
}
setup()

export { prepareTexts, splitText, wrapLines, storeOriginalTexts }
